import React from "react";

/**
 * KalkiIntelligenceDock — the premium floating purple intelligence dock.
 *
 * Six KALKI intelligence layers:
 *   CHARACTER ◎   KARMA ✓   FUTURE ✦   WHY ?   REMEDIES ✧   ME (DP)
 *
 * • Dark translucent purple glass pill, floats at bottom-center.
 * • Selected item: Electric Indigo illumination with fuchsia edge.
 * • Cyber Lime dot = positive/completed. Fuchsia glow = new insight. Indigo pulse = reasoning.
 * • Compact mobile sizing with safe-area awareness. Hides when `collapsed`.
 *
 * Props:
 *   onOpen(id)      — fires when a layer is tapped.
 *   active          — currently open sheet id, highlights that icon.
 *   badges          — { karma: 'lime' | null, future: 'fuchsia' | null, why: 'pulse' | null, me: 'dot' | null }
 *   user            — for ME avatar.
 *   collapsed       — hides the dock (e.g. keyboard open).
 *   compact         — small mobile layout.
 */

const INDIGO = "#5D00FF";
const FUCHSIA = "#FF2E9A";
const LIME = "#CFFF04";

export const INTEL_LAYERS = [
  { id: "character", label: "CHARACTER", icon: "◎", title: "My Character",  subtitle: "WHO AM I BECOMING?" },
  { id: "karma",     label: "KARMA",     icon: "✓", title: "My Karma",      subtitle: "WHAT IS MINE TO DO?" },
  { id: "future",    label: "FUTURE",    icon: "✦", title: "My Future",     subtitle: "WHAT MAY LIE AHEAD?" },
  { id: "why",       label: "WHY",       icon: "?", title: "Why?",          subtitle: "WHY DO I SEE THIS?" },
  { id: "remedies",  label: "REMEDIES",  icon: "✧", title: "Remedies",      subtitle: "WHAT CAN I DO NOW?" },
  { id: "me",        label: "ME",        icon: "●", title: "Me",            subtitle: "MY JOURNEY WITH KALKI" },
];

export default function KalkiIntelligenceDock({
  onOpen,
  active = null,
  badges = {},
  user = null,
  collapsed = false,
  compact = false,
}) {
  if (collapsed) return null;

  const items = INTEL_LAYERS;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[60] pointer-events-none"
      style={{
        bottom: `calc(14px + env(safe-area-inset-bottom))`,
        width: "min(640px, calc(100vw - 24px))",
      }}
      data-testid="kalki-dock"
    >
      <div
        className="pointer-events-auto flex items-end justify-between rounded-full"
        style={{
          padding: compact ? "8px 10px" : "10px 14px",
          background: "rgba(20, 12, 35, 0.72)",
          border: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(22px) saturate(140%)",
          WebkitBackdropFilter: "blur(22px) saturate(140%)",
          boxShadow:
            "0 20px 60px -20px rgba(93,0,255,0.4), 0 2px 0 rgba(255,255,255,0.04) inset, 0 -1px 0 rgba(0,0,0,0.3) inset",
        }}
      >
        {items.map((it) => {
          const isActive = active === it.id;
          const badge = badges[it.id] || null;
          const isMe = it.id === "me";
          return (
            <button
              key={it.id}
              type="button"
              data-testid={`dock-${it.id}`}
              onClick={() => onOpen?.(it.id)}
              className="group relative flex flex-col items-center justify-end transition-transform"
              style={{
                flex: 1,
                padding: compact ? "4px 2px" : "6px 4px",
                minWidth: compact ? 44 : 52,
                minHeight: 48,
                color: isActive ? "#F6F1FF" : "rgba(230,220,255,0.68)",
                transform: isActive ? "translateY(-4px)" : "translateY(0)",
              }}
            >
              {/* purple light halo under active icon */}
              {isActive && (
                <span
                  aria-hidden
                  className="absolute inset-x-2 -bottom-1 pointer-events-none rounded-full"
                  style={{
                    height: 22,
                    background: `radial-gradient(ellipse at center, ${INDIGO}55 0%, ${INDIGO}00 65%)`,
                    filter: "blur(2px)",
                  }}
                />
              )}

              {/* Icon */}
              <span
                className="flex items-center justify-center transition-colors"
                style={{
                  width: compact ? 24 : 28,
                  height: compact ? 24 : 28,
                  fontSize: compact ? 15 : 17,
                  lineHeight: 1,
                  fontWeight: 300,
                  color: isActive ? "#FFFFFF" : "rgba(230,220,255,0.78)",
                  textShadow: isActive ? `0 0 10px ${INDIGO}99, 0 0 2px ${FUCHSIA}88` : "none",
                  position: "relative",
                }}
              >
                {isMe && user?.picture ? (
                  <span
                    className="rounded-full"
                    style={{
                      width: compact ? 22 : 26,
                      height: compact ? 22 : 26,
                      background: `url(${user.picture}) center/cover`,
                      border: isActive ? `1.5px solid #FFFFFF` : `1.5px solid rgba(255,255,255,0.3)`,
                      boxShadow: isActive ? `0 0 10px ${INDIGO}99` : "none",
                    }}
                  />
                ) : isMe ? (
                  <span
                    className="rounded-full flex items-center justify-center text-[10px] font-semibold"
                    style={{
                      width: compact ? 22 : 26,
                      height: compact ? 22 : 26,
                      background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`,
                      color: "#FFFFFF",
                      border: isActive ? "1.5px solid #FFFFFF" : "1.5px solid rgba(255,255,255,0.3)",
                      boxShadow: isActive ? `0 0 10px ${INDIGO}99` : "none",
                    }}
                  >
                    {(user?.name || "You").slice(0, 1).toUpperCase()}
                  </span>
                ) : (
                  it.icon
                )}

                {/* Contextual badge */}
                {badge && (
                  <span
                    aria-hidden
                    className="absolute -top-0.5 -right-0.5 rounded-full"
                    style={{
                      width: 6,
                      height: 6,
                      background:
                        badge === "lime" ? LIME :
                        badge === "fuchsia" ? FUCHSIA :
                        badge === "pulse" ? INDIGO :
                        "rgba(255,255,255,0.6)",
                      boxShadow:
                        badge === "lime" ? `0 0 6px ${LIME}` :
                        badge === "fuchsia" ? `0 0 8px ${FUCHSIA}` :
                        badge === "pulse" ? `0 0 8px ${INDIGO}` :
                        "none",
                      animation: badge === "pulse" ? "kalki-dock-pulse 1.8s ease-in-out infinite" : "none",
                    }}
                  />
                )}
              </span>

              {/* Label */}
              <span
                className="mt-1 tracking-[0.16em]"
                style={{
                  fontSize: compact ? 8.5 : 9.5,
                  fontWeight: 500,
                  letterSpacing: "0.18em",
                  color: isActive ? "#F6F1FF" : "rgba(230,220,255,0.55)",
                  textTransform: "uppercase",
                  lineHeight: 1,
                }}
              >
                {it.label}
              </span>
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes kalki-dock-pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}
